import { fetchProviderSpaces } from "services/dashboard.services";
import { deleteSpace } from "services/addSpace.services";
import { selectAuthToken } from "store/auth/selectors";
import ProviderSpaceCard from "./ProviderSpaceCard";
import { ReusableConfirmationModal } from "modals";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  FormButton,
  NoResultsSection,
  Pagination,
  ProviderSpaceCardSkeleton,
} from "reusableComponents";
import { useModal } from "context";
import LastMinuteConfirmationModal from "modals/LastMinuteConfirmationModal";
import { markLastMinute, updateSpace } from "services/spaceManagement.services";

import styles from "./styles.module.scss";
import CTAButton from "modules/Header/components/CTAButton";
import { SettingsIconSvg } from "assets/icons/svgComponents";

const MySpaces = () => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal(); // Get closeModal from context
  const { t } = useTranslation("global");
  const [error, setError] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessOpacity, setLessOpacity] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);

  const itemsPerPage = 8;
  const accessToken = useSelector(selectAuthToken);

  useEffect(() => {
    const loadSpaces = async () => {
      try {
        setLoading(true);
        const data = await fetchProviderSpaces(
          accessToken,
          currentPage,
          itemsPerPage
        );
        setSpaces(data.data);
        setPaginationData(data.pagination);
      } catch (err) {
        setError("Error fetching provider spaces");
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      loadSpaces();
    }, 300);

    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => clearTimeout(timeoutId);
  }, [accessToken, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const goToAddSpacePage = () => {
    setLessOpacity(true);
    setTimeout(() => {
      setLessOpacity(false);
      navigate(`/create-space/step1`);
    }, 500);
  };

  const handleEdit = (spaceId) => {
    setLessOpacity(true);
    setTimeout(() => {
      setLessOpacity(false);
      navigate(`/create-space/step1/${spaceId}`);
    }, 500);
  };

  const handleEditOptions = (spaceId) => {
    setLessOpacity(true);
    setTimeout(() => {
      setLessOpacity(false);
      navigate(`/create-space/step4/${spaceId}`);
    }, 500);
  };

  const handleViewSpace = (spaceId) => {
    window.open(`/space/${spaceId}`, '_blank');
  };

  const handleDelete = async (spaceId) => {
    try {
      await deleteSpace(spaceId, accessToken);
      
      // Update spaces list after successful deletion
      setSpaces((prevSpaces) => {
        const filteredSpaces = prevSpaces.filter((space) => space.id !== spaceId);
        
        // Handle pagination when last item on page is deleted
        if (filteredSpaces.length === 0 && currentPage > 1) {
          setCurrentPage((prevPage) => prevPage - 1);
        }
        
        return filteredSpaces;
      });

      // Close modal after successful deletion
      closeModal();
    } catch (error) {
      console.error("Failed to delete space:", error);
      // Re-throw error so modal can display it
      throw error;
    }
  };

  const confirmDelete = (spaceId) => {
    openModal({
      content: (
        <ReusableConfirmationModal
          message={t("dashboard.spacesManagement.confirmationText")}
          onConfirm={() => handleDelete(spaceId)}
        />
      ),
      title: t("dashboard.spacesManagement.confirmationTitle"),
    });
  };

  const handleStatusChange = async (
    spaceId,
    value,
    formData = [],
    isLastMinute = false
  ) => {
    setLessOpacity(true);
    try {
      if (typeof value === "boolean") {
        await updateSpace(spaceId, { private: value }, accessToken);
        setSpaces((prevSpaces) =>
          prevSpaces.map((s) =>
            s.id === spaceId ? { ...s, private: value } : s
          )
        );
      } else {
        await markLastMinute(spaceId, formData, accessToken);
        setSpaces((prevSpaces) =>
          prevSpaces.map((s) =>
            s.id === spaceId ? { ...s, last_minute: true } : s
          )
        );
      }
      
      // Close modal after successful status change
      closeModal();
    } catch (error) {
      console.error("Error updating space status:", error);
      // Re-throw error so modal can display it
      throw error;
    } finally {
      setLessOpacity(false);
    }
  };

  const handleConfirmStatusChange = (spaceId, value, isLastMinute) => {
    if (value === true) {
      openModal({
        content: (
          <ReusableConfirmationModal
            message={t("dashboard.spacesManagement.confirmationOfflineText")}
            onConfirm={() => handleStatusChange(spaceId, value)}
          />
        ),
        title: t("dashboard.spacesManagement.confirmationTitle"),
      });
    } else if (value === false) {
      handleStatusChange(spaceId, value, [], isLastMinute);
    } else if (isLastMinute) {
      handleStatusChange(spaceId, false, [], isLastMinute);
    } else if (value === "lastMinute") {
      openModal({
        content: (
          <LastMinuteConfirmationModal
            message={t("dashboard.promoCodesManagement.confirmationOfflineText")}
            onConfirm={async (formData) =>
              handleStatusChange(spaceId, value, formData)
            }
          />
        ),
        title: t("_"),
      });
    }
  };

  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div
      className={`${styles.container} ${lessOpacity ? styles.lessOpacity : ""}`}
    >
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>
          {t("dashboard.provider.spaceManagement")}
        </h2>
        <div className={styles.addNewSpaceBtnHolder}>
          <CTAButton
            variant="light"
            onClick={() => navigate("/dashboard/groups")}
            aria-label={t("calendar.groups.manage")}
            icon={<SettingsIconSvg />}
          >
            {t("calendar.menu.groupsManagement")}
          </CTAButton>
          <CTAButton
            children={`+ ${t("dashboard.provider.addSpace")}`}
            onClick={goToAddSpacePage}
          />
        </div>
      </div>
      
      <div className={styles.list}>
        {loading ? (
          [...Array(5)].map((_, index) => (
            <ProviderSpaceCardSkeleton key={index} />
          ))
        ) : spaces.length > 0 ? (
          spaces.map((space) => (
            <ProviderSpaceCard
              last_step={space.last_step}
              isLastMinute={space.last_minute}
              isSpacePrivate={space.private}
              isSpaceCompleted={space.completed}
              key={space.id}
              imageUrl={space.images[0]?.image_url}
              name={space.name}
              maxCapacity={space.max_capacity}
              spaceCategory={space?.placeGroup || null}
              status={space.status}
              onEdit={() => handleEdit(space.id)}
              onViewSpace={() => handleViewSpace(space.id)}
              onDelete={() => confirmDelete(space.id)}
              onEditOptions={() => handleEditOptions(space.id)}
              onStatusChange={(value) =>
                handleConfirmStatusChange(space.id, value, space.last_minute)
              }
              lockAvailable={space.lockAvailable}
            />
          ))
        ) : (
          <NoResultsSection
            title={t("dashboard.spacesManagement.noResultTitle")}
            text={t("dashboard.spacesManagement.noResultText")}
          />
        )}
      </div>

      {paginationData && (
        <Pagination
          totalItems={paginationData.total}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          hasMorePages={paginationData.has_more_pages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default MySpaces;